import {
  OrderSummary,
  OrderView,
  OrderShipmentSummary,
  InstallationTravelInformation,
} from './order';
import { OrderFilter, UnconfirmedShipmentsFilter } from './filterConfiguration';
import { apiFetch } from '@ami/react/core';
import { NotificationManager } from 'react-notifications';
import { NotificationScheduleTypes } from '../orders/order';

const ordersUrl = 'orders';
const ordersUrlBlackbird = 'sales/orders';
const ordersViewUrl = 'orders/view';
const orderShipmentsUrl = 'order-shipments';
const orderInstallationUrl = 'order-installation';
const orderSerialNumbersUrl = 'order-serial-numbers';
const orderCustomerApproval = 'customer-approvals';

function searchOrders(filter, page) {
  const payload = new OrderFilter(filter.toPayload());
  if (filter.isForRecentWidget) {
    payload.$top = 4;
  } else {
    payload.setPaginationParameters(page);
  }
  return apiFetch(`${ordersUrl}?${payload.toQueryString()}`).then((orders) =>
    orders.map((x) => new OrderSummary(x))
  );
}

function downloadDetailsCsv(filter) {
  const payload = new OrderFilter(filter.toPayload());
  return apiFetch(`${ordersViewUrl}/csv/details?${payload.toQueryString()}`, 'GET', null, {
    contentType: 'text/csv',
  });
}

function getViewById(id) {
  return apiFetch(`${ordersViewUrl}/${id}`).then((x) => new OrderView(x));
}

function downloadPdf(id) {
  return apiFetch(`${ordersViewUrl}/${id}/pdf`, 'GET', null, {
    contentType: 'application/pdf',
  });
}

function confirmShipment(orderId, shipmentId, confirmationData) {
  return apiFetch(
    `${orderShipmentsUrl}/${orderId}/confirm/${shipmentId}`,
    'PUT',
    confirmationData
  );
}

function markShipped(orderId, shipmentId, shippingData) {
  return apiFetch(
    `${orderShipmentsUrl}/${orderId}/markshipped/${shipmentId}`,
    'PUT',
    shippingData
  );
}

function unshipShipment(orderId, shipmentId) {
  return apiFetch(
    `${orderShipmentsUrl}/${orderId}/unship/${shipmentId}`,
    'PUT'
  );
}

function createOrder(data) {
  return apiFetch(ordersUrl, 'POST', data.order, {
    automaticSuccessNotification: false,
  }).then((result) => {
    NotificationManager.success('Order created');
    return result.id;
  });
}
function createOrderBlackbird(data) {
  return apiFetch(ordersUrlBlackbird, 'POST', data.order, {
    automaticSuccessNotification: false,
  }).then((result) => {
    NotificationManager.success('Order created'); 
    return result.id;
  });
}

function updateOrderBlackbird(data) {
  return apiFetch(`${ordersUrlBlackbird}`, 'PUT', data, {
    automaticSuccessNotification: false,
  }).then((result) => {
    NotificationManager.success('Order updated');
    return result.id;
  });
}

function updateOrder(data) {
  return apiFetch(`${ordersUrl}/${data.orderId}`, 'PUT', data.order, {
    automaticSuccessNotification: false,
  }).then((result) => {
    NotificationManager.success('Order updated');
    return result.id;
  });
}

function cancel(id) {
  return apiFetch(`${ordersUrl}/${id}/cancel`, 'PUT', null, {
    automaticSuccessNotification: false,
  }).then(() => NotificationManager.success('Order canceled'));
}

function getUnconfirmedShipments(filter, page) {
  const payload = new UnconfirmedShipmentsFilter(filter.toPayload());
  payload.setPaginationParameters(page);
  return apiFetch(
    `${orderShipmentsUrl}/unconfirmed?${payload.toQueryString()}`
  ).then((shipments) => shipments.map((x) => new OrderShipmentSummary(x)));
}

function confirmShipments(shipments, sendNotification) {
  return apiFetch(`${orderShipmentsUrl}/confirm-bulk`, 'PUT', {
    shipments,
    sendNotification,
  });
}

async function getInstallationTravelInformation(dealerBranchId, priceZoneId) {
  const result = await apiFetch(
    `${orderInstallationUrl}/information/${dealerBranchId}/${priceZoneId}`
  );
  return result ? new InstallationTravelInformation(result) : null;
}

function addCustomActivity(id, message) {
  return apiFetch(
    `${ordersUrl}/${id}/activities`,
    'POST',
    { message },
    {
      automaticSuccessNotification: false,
    }
  ).then(() => NotificationManager.success('Custom activity added'));
}

function updateShipments(id, data) {
  return apiFetch(`${orderShipmentsUrl}/${id}/update-shipments`, 'PUT', data, {
    automaticSuccessNotification: false,
  }).then(() => NotificationManager.success('Shipments updated'));
}

function editSerialNumber(id, orderLineItemId, serialNumber, serialNumberEdit) {
  return apiFetch(
    `${orderSerialNumbersUrl}/${id}/orderLineItem/${orderLineItemId}/update/${serialNumber}`,
    'PUT',
    { serialNumberEdit },
    {
      automaticSuccessNotification: false,
    }
  ).then(() => NotificationManager.success('Serial number updated'));
}

function addCustomerApproval(data) {
  return apiFetch(`${orderCustomerApproval}`, 'POST', data, {
    automaticSuccessNotification: false,
  }).then(() => NotificationManager.success('Customer Approval added'));
}

async function getNotificationSchedules() {
  return Object.keys(NotificationScheduleTypes).map((key) => ({
    id: NotificationScheduleTypes[key].id,
    label: NotificationScheduleTypes[key].label,
  }));
}

function addRequestNewLShipDate(orderId, notes) {
  return apiFetch(
    `${orderShipmentsUrl}/${orderId}/request-new-ship-date`,
    'POST',
    { notes },
    {
      automaticSuccessNotification: false,
    }
  )
  .then(() => NotificationManager.success('New ship date requested'));
}

const ordersService = {
  searchOrders,
  downloadDetailsCsv,
  cancel,
  getViewById,
  createOrder,
  createOrderBlackbird,
  updateOrderBlackbird,
  updateOrder,
  downloadPdf,
  confirmShipment,
  markShipped,
  unshipShipment,
  getUnconfirmedShipments,
  confirmShipments,
  getInstallationTravelInformation,
  addCustomActivity,
  updateShipments,
  editSerialNumber,
  addCustomerApproval,
  getNotificationSchedules,
  addRequestNewLShipDate
};
export { ordersService };
